import { SwapOrReturnToResolveData, SwapOrReturnTypeIds, CountTypeEnum } from '../../SwapOrReturnTypes'
import * as S from './ResolveSwapOrReturnTable.styles'
import { useMemo, useState } from 'react'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import AddIcon from '@mui/icons-material/Add'
import TableMui from 'src/components/TableMui'
import { Box, Tooltip, Typography } from '@mui/material'
import ModalCountProductQuantityReturn from '../ModalCountProductQuantityReturn'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { useDispatch } from 'react-redux'
import { actions } from '../../SwapOrReturnState'
import ModalCountProductQuantityReceive from '../ModalCountProductQuantityReceive'
import { getReceivementAgreementInfo } from 'src/utils/getReceivementAgreementInfo'

interface ResolveSwapOrReturnCardProps {
  swapOrReturnToResolve: SwapOrReturnToResolveData
  inResolution: boolean
  tableHeight: string
}

export const ResolveSwapOrReturnTable = ({
  swapOrReturnToResolve,
  inResolution,
  tableHeight,
}: ResolveSwapOrReturnCardProps) => {
  const dispatch = useDispatch()

  const [showCountReturnModal, setShowCountReturnModal] = useState(false)
  const [showCountReceiveModal, setShowCountReceiveModal] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined)

  const [showUndoModal, setShowUndoModal] = useState<boolean>(false)

  const countQuantity = (index: number, type: CountTypeEnum) => {
    if (!inResolution) {
      return
    }

    setSelectedIndex(index)

    if (type === CountTypeEnum.RETURN) {
      setShowCountReturnModal(true)
    } else if (type === CountTypeEnum.RECEIVE) {
      setShowCountReceiveModal(true)
    }
  }

  const confirmUndoCount = (index: number) => {
    if (!inResolution) {
      return
    }
    setSelectedIndex(index)
    setShowUndoModal(true)
  }

  const undoCount = () => {
    if (selectedIndex === undefined) {
      return
    }
    dispatch(actions.undoItemCount(swapOrReturnToResolve.items[selectedIndex].idItem))
    setShowUndoModal(false)
  }

  const removeDuplicatedAddressesAndFirstElement = (addresses: string[]) => addresses.filter((elem, idx, self) => idx !== 0 && idx === self.indexOf(elem))

  const resolveTableColumns = useMemo(() => {
    const columns = [
      { id: 'productCode', label: 'Cod. Produto', testId: 'product-code-column-test-id' },
      { id: 'productDescription', label: 'Desc. Produto', testId: 'product-description-column-test-id' },
      {
        id: 'quantityToReturn', label: 'Qtd. a retornar/retornado', testId: 'quantity-to-return-column-test-id', useSorting: false,
      },
      {
        id: 'quantityToReceive', label: 'Qtd. a receber/recebido', testId: 'quantity-to-receive-column-test-id', useSorting: false,
      },
      { id: 'address', label: 'Endereço', testId: 'address-column-test-id' },
      {
        id: 'undo', label: 'Desfazer', testId: 'undo-column-test-id', useSorting: false,
      },
    ]
    if (swapOrReturnToResolve.idType === SwapOrReturnTypeIds.RETURN) {
      columns.splice(3, 1)
    }
    return columns
  }, [swapOrReturnToResolve])

  const resolveTableRows = useMemo(() => swapOrReturnToResolve.items.map((product, index) => ({
    ...product,
    productCode: product.productCode,
    productDescription: product.productDescription,
    quantityToReturn:
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: '.5rem',
    }}
  >
    {product.notFound ? (
      <span>CANCELADO</span>
    ) : (
      <>
        <span>{`${product.returnQuantity} ${product.measureUnit} / ${product.returnBatches.reduce((acc, batch) => acc + (batch.countedQuantity || 0), 0)} ${product.measureUnit}`}</span>
        <Tooltip
          title='Inicie a resolução da pendência para realizar a contagem'
          placement='top'
          disableHoverListener={inResolution}
          arrow
        >
          <S.DivIconButton>
            <S.CustomIconButton
              onClick={() => countQuantity(index, CountTypeEnum.RETURN)}
              data-testid='count-return-button-test-id'
              disabled={!inResolution}
            >
              <AddIcon sx={{ fontSize: '20px' }} />
            </S.CustomIconButton>
          </S.DivIconButton>
        </Tooltip>
      </>
    )}
  </Box>,
    quantityToReceive:
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: '.5rem',
    }}
  >
    {product.notFound ? (
      <span>CANCELADO</span>
    ) : (
      <>
        <span>{`${product.receiveQuantity || 0} ${product.measureUnit} / ${product.receiveBatches?.reduce((acc, batch) => acc + (batch.quantity || 0), 0) || 0} ${product.measureUnit}`}</span>
        <Tooltip
          title={
        !inResolution ? 'Inicie a resolução da pendência para realizar a contagem' : 'Faça a contagem da quantidade a retornar primeiro'
      }
          placement='top'
          disableHoverListener={inResolution && product.returnQuantityCounted}
          arrow
        >
          <S.DivIconButton>
            <S.CustomIconButton
              onClick={() => countQuantity(index, CountTypeEnum.RECEIVE)}
              data-testid='count-receive-button-test-id'
              disabled={!inResolution || !product.returnQuantityCounted}
            >
              <AddIcon sx={{ fontSize: '20px' }} />
            </S.CustomIconButton>
          </S.DivIconButton>
        </Tooltip>
      </>
    )}
  </Box>,
    address:
  <Box>
    <Tooltip
      title={(
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {removeDuplicatedAddressesAndFirstElement(product.addresses).map((address, idx) => <span key={idx}>{address}</span>)}
        </Box>
      )}
      placement='top'
      disableHoverListener={removeDuplicatedAddressesAndFirstElement(product.addresses).length === 0}
      arrow
    >
      <span>{product.addresses[0]}</span>
    </Tooltip>
  </Box>,
    undo:
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
    }}
  >
    <Tooltip
      title={!inResolution
        ? 'Inicie a resolução de pendência habilitar o desfazer'
        : 'Faça a contagem para habilitar o desfazer'}
      placement='top'
      disableHoverListener={inResolution && (product.returnQuantityCounted || !!product.receiveQuantityCounted)}
      arrow
    >
      <S.DivIconButton>
        <S.CustomIconButton
          onClick={() => confirmUndoCount(index)}
          data-testid='undo-button-test-id'
          disabled={!inResolution || (!product.returnQuantityCounted && !product.receiveQuantityCounted)}
        >
          <UndoRoundedIcon sx={{ fontSize: '20px' }} />
        </S.CustomIconButton>
      </S.DivIconButton>
    </Tooltip>
  </Box>,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [inResolution, swapOrReturnToResolve.items])

  return (
    <S.Container>
      <TableMui
        containerStyle={{ marginTop: '1rem' }}
        thinLine
        columns={resolveTableColumns}
        emptyLabel='Não há produtos para resolução'
        rows={resolveTableRows}
        enablePagination
        rowsPerPageLimit={25}
        minHeight={tableHeight}
        maxHeight={tableHeight}
        hasSorting
        testId='swap-or-return-resolution-table-test-id'
      />
      {selectedIndex !== undefined && (
        <>
          <ModalCountProductQuantityReturn
            show={showCountReturnModal}
            swapOrReturnTypeId={swapOrReturnToResolve.idType}
            infoSupplier={swapOrReturnToResolve.infoReturnOptions && getReceivementAgreementInfo(swapOrReturnToResolve.infoReturnOptions).infoSupplier}
            product={swapOrReturnToResolve.items[selectedIndex]}
            onConfirm={() => setShowCountReturnModal(false)}
            onClose={() => setShowCountReturnModal(false)}
          />
          <ModalCountProductQuantityReceive
            show={showCountReceiveModal}
            product={swapOrReturnToResolve.items[selectedIndex]}
            onConfirm={() => setShowCountReceiveModal(false)}
            onClose={() => setShowCountReceiveModal(false)}
          />
        </>
      )}
      <ConfirmOrRejectModal
        show={showUndoModal}
        title='Desfazer contagem'
        customizableDescription={(
          <S.BodyModalAlert>
            <S.AlertMessageBox>
              <Typography variant='body1'>
                Ao prosseguir com a ação, todas as contagens realizadas para esse produto serão desfeitas.
              </Typography>
              <Typography variant='body1'>
                Deseja continuar?
              </Typography>
            </S.AlertMessageBox>
          </S.BodyModalAlert>
        )}
        onConfirm={() => undoCount()}
        onReject={() => setShowUndoModal(false)}
        onClose={() => setShowUndoModal(false)}
        dataTestId='undo-count-modal-test-id'
      />
    </S.Container>
  )
}
